import { PlayArrow } from "@material-ui/icons";
import { Box, Button, MenuItem, Select, Typography } from "@mui/material";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

PlannedSessionsBar.propTypes = {
    sessions: PropTypes.array,
    onClickStartSession: PropTypes.func,
    updateSessionStatus: PropTypes.func,
};
function PlannedSessionsBar({ sessions, onClickStartSession, updateSessionStatus }) {
    const [selectedSession, setSelectedSession] = useState(null);
    const [timeLeft, setTimeLeft] = useState(null);
    const [timer, setTimer] = useState(null);
    const { t } = useTranslation();

    const formatTimeLeft = () => timeLeft > 0
        ? (() => {
            const days = Math.floor(timeLeft / 86400);
            const hours = Math.floor((timeLeft % 86400) / 3600);
            const minutes = Math.floor((timeLeft % 3600) / 60);
            const seconds = timeLeft % 60;
            const parts = [];
            if (days > 0) parts.push(`${days}`);
            if (hours > 0 || days > 0) parts.push(`${hours.toString().padStart(2, '0')}`);
            if (minutes > 0 || hours > 0 || days > 0) parts.push(`${minutes.toString().padStart(2, '0')}`);
            parts.push(`${seconds.toString().padStart(2, '0')}`);
            return `${parts.join(':')}`;
        })()
        : 'Session can start'


    useEffect(() => {
        const mostRecentSession = sessions.reduce((prev, current) => (dayjs(`${current.sessionDate} ${current.sessionTime}`).isAfter(dayjs(`${prev.sessionDate} ${prev.sessionTime}`)) ? current : prev), sessions[0]);
        setSelectedSession(mostRecentSession);
        setTimeLeft(null);
        if (timer) {
            clearInterval(timer);
            setTimer(null);
        }
    }, [sessions]);

    useEffect(() => {
        if (!selectedSession) {
            setTimeLeft(null);
            if (timer) {
                clearInterval(timer);
                setTimer(null);
            }
            return () => { };
        }

        const calculateTimeLeft = () => {
            const now = dayjs();
            const sessionTime = dayjs.utc(`${selectedSession.sessionDate} ${selectedSession.sessionTime}`);
            const diff = sessionTime.diff(now, 'second');
            return diff;
        }

        const initialTimeLeft = calculateTimeLeft();
        if (initialTimeLeft <= 0) {
            setTimeLeft(null);
            return () => { };
        }

        const process = () => {
            const diff = calculateTimeLeft();
            if (diff <= 0) {
                if (selectedSession.status !== 'STARTED' && selectedSession.sessionKind !== 'ONLINE') {
                    updateSessionStatus(selectedSession.id, 'STARTED');
                }
                setTimeLeft(null);
                clearInterval(timer);
                setTimer(null);
                return;
            }
            setTimeLeft(diff);
        }
        const interval = setInterval(process, 1000);
        setTimer(interval);

        process();

        return () => clearInterval(interval);
    }, [selectedSession, timer, updateSessionStatus]);


    return (
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} paddingX={{ xs: 0, lg: 4 }}>
            {
                selectedSession
                    ? (
                        <Select
                            id="session-select"
                            value={selectedSession.id}
                            onChange={(e) => setSelectedSession(sessions.find(s => s.id === e.target.value))}
                            size="small"
                            disableUnderline
                            renderValue={(value) => {
                                const selected = sessions.find(s => s.id === value) || selectedSession;

                                if (!selected) return '-';

                                return (
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <img src={selected.image} alt={selected.name} style={{ aspectRatio: 1, borderRadius: 8, maxWidth: 40 }} />
                                        <Box>
                                            <Typography variant="body1">{selected.name}</Typography>
                                            <Typography variant="caption">{
                                                t(`ses_kind.${selected.sessionKind}`)
                                            }</Typography>
                                        </Box>
                                    </Box>
                                );
                            }}
                            sx={{ boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { borderStyle: 'none' } }}
                        >
                            {
                                sessions.map((session) => (
                                    <MenuItem key={session.id} value={session.id}>{session.name}</MenuItem>
                                ))
                            }
                        </Select>
                    )
                    : <Typography variant="body1" color="text.secondary">
                        {t('no_session_av')}
                    </Typography>
            }
            {/* Timer */}
            {
                timeLeft !== null && timeLeft > 0
                    ? <Box marginRight={2}>
                        <Typography variant="caption">
                            {t('time_left')}
                        </Typography>
                        <Typography variant="h6" component="div">
                            {timeLeft === null ? '-' : formatTimeLeft()}
                        </Typography>
                    </Box>
                    : (selectedSession?.status && selectedSession.status.toLowerCase() === 'STARTED') || selectedSession?.sessionKind !== 'ONLINE'
                        ? <Typography variant="caption" color="text.secondary">
                            {t('ses_state.STARTED')}
                        </Typography>
                        : <Button size="small" variant="contained" color="primary" onClick={() => onClickStartSession(selectedSession)} sx={{ padding: '6px 16px', fontSize: '0.875rem' }}>
                            <PlayArrow style={{ fontSize: '1.25rem', marginRight: '.1rem' }} />
                            {t('start_session')}
                        </Button>
            }
        </Box>
    );
}

export default PlannedSessionsBar;